import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import Select from 'react-select';
import PhotoUpload from './PhotoUpload';

const defaultRow = { id: null, query: '', maxItems: 5, timeFilter: 'any', site: '', locations: [] };

const QueriesSection = ({ authUser, isDarkMode }) => {
  const [rows, setRows] = useState([ { ...defaultRow } ]);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [masterUserId, setMasterUserId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [expandedField, setExpandedField] = useState('locations');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const [bannedKeywords, setBannedKeywords] = useState([]);
  const [bannedInput, setBannedInput] = useState('');
  const [loadingBanned, setLoadingBanned] = useState(true);
  const [savingBanned, setSavingBanned] = useState(false);
  const [bannedError, setBannedError] = useState('');

  const loadUsers = async () => {
    if (!authUser?.is_admin) return;
    try {
      const res = await axios.get('/api/users');
      const list = Array.isArray(res.data) ? res.data : [];
      setUsers(list);
      const me = list.find(u=>u.username===authUser.username);
      setSelectedUserId(me?.id || null);
      try {
        const mu = await axios.get('/api/queries/master_user');
        setMasterUserId(mu.data?.id || null);
      } catch { setMasterUserId(null); }
    } catch {}
  };

  const loadLocations = async () => {
    try {
      const res = await axios.get('/api/locations');
      const list = Array.isArray(res.data) ? res.data : [];
      setLocations(list);
    } catch {}
  };

  const loadBannedKeywords = async () => {
    try {
      setLoadingBanned(true);
      const res = await axios.get('/api/banned_keywords');
      const list = Array.isArray(res.data) ? res.data : [];
      setBannedKeywords(list);
      setBannedError('');
    } catch (err) {
      setBannedKeywords([]);
      setBannedError('Failed to load banned keywords');
    } finally {
      setLoadingBanned(false);
    }
  };

  const loadQueries = useCallback(async (uid) => {
    setLoadingSaved(true);
    isInitialLoadRef.current = true;
    try {
      const res = await axios.get('/api/queries', { params: authUser?.is_admin && uid ? { user_id: uid } : {} });
      const arr = Array.isArray(res.data) ? res.data : [];
      if (arr.length > 0) {
        setRows(arr.map(a => ({ 
          id: a.id || null,
          query: a.query || '', 
          maxItems: a.maxItems || 5,
          timeFilter: a.timeFilter || 'any',
          site: a.site || '',
          locations: a.locations || []
        })));
      } else {
        setRows([ { ...defaultRow } ]);
      }
    } catch {
      setRows([ { ...defaultRow } ]);
    }
    setLoadingSaved(false);
    // Set to false after a brief delay to allow state updates to complete
    setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 100);
  }, [authUser]);

  useEffect(() => {
    if (authUser?.is_admin) {
      loadUsers();
    }
    loadLocations();
    if (authUser?.is_admin) {
      loadBannedKeywords();
    } else {
      setLoadingBanned(false);
      setBannedKeywords([]);
    }
    // eslint-disable-next-line
  }, [authUser]);

  useEffect(() => {
    if (authUser?.is_admin) {
      if (selectedUserId) loadQueries(selectedUserId);
    } else {
      loadQueries(null);
    }
    // eslint-disable-next-line
  }, [selectedUserId, authUser]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const saveQueries = useCallback(async (queriesToSave) => {
    if (isInitialLoadRef.current) return; // Don't save during initial load
    
    try {
      setSaving(true);
      
      // Separate empty rows from rows to save
      const emptyRows = queriesToSave.filter(r => 
        (r.query || '').trim().length === 0 && 
        (r.site || '').trim().length === 0 && 
        (r.locations || []).length === 0
      );
      
      const queries = queriesToSave
        .map(r => ({
          id: r.id || null,  // Include ID so backend knows which query to update
          query: (r.query || '').trim(),
          maxItems: parseInt(r.maxItems || 0, 10) || 5,
          timeFilter: r.timeFilter || 'any',
          site: (r.site || '').trim(),
          locations: r.locations || []
        }))
        .filter(q => q.query.length > 0 || q.site.length > 0 || q.locations.length > 0); // Save even if query is empty but other fields are set
      
      const payload = { queries };
      const params = authUser?.is_admin && selectedUserId ? { params: { user_id: selectedUserId } } : {};
      await axios.post('/api/queries/save', payload, params);
      
      // Reload to get IDs for new queries (backend now preserves query IDs and photos)
      isInitialLoadRef.current = true;
      const targetUserId = authUser?.is_admin && selectedUserId ? selectedUserId : null;
      await loadQueries(targetUserId);
      
      // Append empty rows back after reload
      if (emptyRows.length > 0) {
        setRows(prevRows => [...prevRows, ...emptyRows]);
      }
      
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    } catch (err) {
      console.error('Failed to auto-save queries:', err);
      // Don't show error message for auto-save failures
    } finally {
      setSaving(false);
    }
  }, [authUser, selectedUserId, loadQueries]);

  const update = (i, field, value) => {
    const copy = rows.slice();
    copy[i] = { ...copy[i], [field]: value };
    setRows(copy);
    
    // Auto-save with debouncing (500ms delay)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveQueries(copy);
    }, 500);
  };

  const handleFieldFocus = (fieldName) => {
    // Only change expansion if clicking on a different field
    if (expandedField !== fieldName) {
      setExpandedField(fieldName);
    }
  };

  // Check if we're on mobile with responsive hook
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addRow = () => {
    const newRows = [...rows, { ...defaultRow }];
    setRows(newRows);
    // Don't auto-save empty rows - let user start typing first
    // Auto-save will trigger when they update any field
  };
  
  const removeRow = (i) => {
    if (rows.length === 1) return; // keep at least one
    const copy = rows.slice();
    copy.splice(i, 1);
    setRows(copy);
    // Auto-save after removing row
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveQueries(copy);
    }, 500);
  };

  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      const copy = rows.slice();
      copy.splice(deleteIndex, 1);
      setRows(copy);
      setShowDeleteConfirm(false);
      setDeleteIndex(null);
      // Auto-save after deleting row
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveQueries(copy);
      }, 500);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteIndex(null);
  };

  const run = async () => {
    setMsg('');
    setRunning(true);
    try {
      const queries = rows
        .map(r => ({
          id: r.id || null,  // Include ID so backend knows which query to update
          query: (r.query || '').trim(),
          maxItems: parseInt(r.maxItems || 0, 10) || 5,
          timeFilter: r.timeFilter || 'any',
          site: (r.site || '').trim(),
          locations: r.locations || []
        }))
        .filter(q => q.query.length > 0);
      const payload = { queries };
      const params = authUser?.is_admin && selectedUserId ? { params: { user_id: selectedUserId } } : {};
      const res = await axios.post('/api/run_queries', payload, params);
      
      if (res.status === 200) {
        const data = res.data;
        if (data.status === 'processing' && data.task_id) {
          setMsg('Queries submitted. Processing in background...');
          // Start polling for task completion
          pollTaskStatus(data.task_id);
        } else {
          setMsg('Queries submitted. Summary generated.');
          setRunning(false);
        }
      } else {
        setMsg('Failed to run queries');
        setRunning(false);
      }
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to run queries');
      setRunning(false);
    }
  };

  const pollTaskStatus = async (taskId) => {
    const pollInterval = 5000; // Poll every 5 seconds (less frequent for long-running tasks)
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await axios.get(`/api/task_status/${taskId}`);
        const data = res.data;
        
        if (data.status === 'completed') {
          setMsg(`Queries completed successfully! Summary ID: ${data.summary_id}`);
          setRunning(false);
        } else if (data.status === 'error') {
          setMsg(`Processing failed: ${data.error}`);
          setRunning(false);
        } else if (data.status === 'running' || data.status === 'starting') {
          setMsg(`Processing...`);
          attempts++;
          // Continue polling indefinitely for long-running tasks
          setTimeout(poll, pollInterval);
        }
      } catch (err) {
        console.error('Task status check failed:', err);
        attempts++;
        // Retry failed requests with exponential backoff
        const retryDelay = Math.min(5000 * Math.pow(1.5, attempts), 30000); // Max 30 seconds
        setMsg(`Checking status... (attempt ${attempts}, retrying in ${Math.round(retryDelay/1000)}s)`);
        setTimeout(poll, retryDelay);
      }
    };

    // Wait a bit before starting to poll to give the background thread time to start
    setTimeout(poll, 1000);
  };

  const setMaster = async (uid) => {
    try {
      await axios.post(`/api/users/${uid}/select_master_query`);
      setMasterUserId(uid);
    } catch {}
  };

  const hasQueries = rows.some(r => (r.query || '').trim().length > 0);

  const addBannedKeyword = async () => {
    if (!authUser?.is_admin) return;
    const trimmed = (bannedInput || '').trim();
    if (!trimmed) return;
    try {
      setSavingBanned(true);
      const res = await axios.post('/api/banned_keywords', { keyword: trimmed });
      setBannedKeywords(prev => [...prev, res.data].sort((a, b) => a.keyword.localeCompare(b.keyword)));
      setBannedInput('');
      setBannedError('');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to add keyword';
      setBannedError(message);
    } finally {
      setSavingBanned(false);
    }
  };

  const removeBannedKeyword = async (id) => {
    if (!authUser?.is_admin) return;
    try {
      setSavingBanned(true);
      await axios.delete(`/api/banned_keywords/${id}`);
      setBannedKeywords(prev => prev.filter(k => k.id !== id));
      setBannedError('');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to remove keyword';
      setBannedError(message);
    } finally {
      setSavingBanned(false);
    }
  };

  return (
    <div>
      {authUser?.is_admin && (
        <div className="card mb-4">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Banned Topics</h5>
          {savingBanned && <span className="text-muted small">Saving…</span>}
        </div>
        <div className="card-body">
          {bannedError && (
            <div className="alert alert-danger py-2 mb-3">{bannedError}</div>
          )}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 320 }}
              placeholder="Add banned keyword"
              value={bannedInput}
              onChange={(e) => setBannedInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addBannedKeyword();
                }
              }}
              disabled={savingBanned}
            />
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={addBannedKeyword}
              disabled={savingBanned || !bannedInput.trim()}
            >
              Add Banned Keyword
            </button>
          </div>
          {loadingBanned ? (
            <div className="text-muted">Loading banned keywords…</div>
          ) : bannedKeywords.length === 0 ? (
            <div className="text-muted">No banned keywords configured. Add keywords to globally exclude lifestyle or off-topic coverage.</div>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {bannedKeywords.map((keyword) => (
                <span className="badge bg-success text-white d-flex align-items-center" key={keyword.id}>
                  <span>{keyword.keyword}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-white ms-2 p-0"
                    onClick={() => removeBannedKeyword(keyword.id)}
                    aria-label={`Remove banned keyword ${keyword.keyword}`}
                    disabled={savingBanned}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <small className="text-muted d-block mt-3">
            Use sparingly. Overuse of banned keywords will greatly reduce story count.
          </small>
        </div>
        </div>
      )}

      <div className="mb-2 d-flex align-items-center justify-content-between">
        <h3 className="mb-0">Queries</h3>
        {authUser?.is_admin && (
          <div className="d-flex align-items-center">
            <div className="me-3">
              <label className="form-label mb-0 me-2">User</label>
              <select className="form-select form-select-sm d-inline-block" style={{ width: 220 }} value={selectedUserId || ''} onChange={e=>setSelectedUserId(Number(e.target.value) || null)}>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.username} {u.id===masterUserId ? '(Master)' : ''}</option>
                ))}
              </select>
            </div>
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="runUserQueries" checked={selectedUserId===masterUserId} onChange={e=> e.target.checked ? setMaster(selectedUserId) : setMaster(null)} />
              <label className="form-check-label" htmlFor="runUserQueries">Run this user's queries</label>
            </div>
          </div>
        )}
      </div>
      {loadingSaved && <div className="alert alert-success py-2">Loading saved queries...</div>}
      <div className="table-responsive">
        {isMobile ? (
          // Mobile: Stacked cards layout
          <div className="row g-3">
            {rows.map((r, i) => (
              <div key={i} className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <label className="form-label small">Time Filter</label>
                        <select 
                          className="form-select form-select-sm" 
                          value={r.timeFilter} 
                          onChange={e=>update(i,'timeFilter',e.target.value)}
                        >
                          <option value="any">Any time</option>
                          <option value="24h">Past day</option>
                          <option value="7d">Past week</option>
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Max Items</label>
                        <input 
                          className="form-control form-control-sm" 
                          type="number" 
                          min={1} 
                          max={100} 
                          value={r.maxItems} 
                          onChange={e=>update(i,'maxItems',e.target.value)} 
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small">Query</label>
                      <input 
                        className="form-control form-control-sm" 
                        value={r.query} 
                        onChange={e=>update(i,'query',e.target.value)} 
                        placeholder="Electric Cooperative" 
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small">Locations</label>
                      <Select
                        isMulti
                        options={locations.map(loc => ({ value: loc.id, label: loc.name }))}
                        value={r.locations.map(locId => ({ 
                          value: locId, 
                          label: locations.find(l => l.id === locId)?.name || 'Unknown' 
                        }))}
                        onChange={(selected) => update(i, 'locations', selected ? selected.map(s => s.value) : [])}
                        placeholder="Select locations..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        theme={isDarkMode ? (theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: '#6c757d',
                            primary75: '#7a8288',
                            primary50: '#8a9299',
                            primary25: '#9aa1a8',
                            danger: '#dc3545',
                            dangerLight: '#f5c6cb',
                            neutral0: '#363636',
                            neutral5: '#404040',
                            neutral10: '#4a4a4a',
                            neutral20: '#545454',
                            neutral30: '#5e5e5e',
                            neutral40: '#686868',
                            neutral50: '#727272',
                            neutral60: '#7c7c7c',
                            neutral70: '#868686',
                            neutral80: '#909090',
                            neutral90: '#9a9a9a',
                          }
                        }) : undefined}
                        styles={isDarkMode ? {
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: '31px',
                            fontSize: '14px',
                            backgroundColor: '#363636',
                            borderColor: state.isFocused ? '#6c757d' : '#545454',
                            boxShadow: state.isFocused ? '0 0 0 1px #6c757d' : 'none',
                            '&:hover': {
                              borderColor: '#6c757d'
                            }
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px'
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: '0px',
                            color: '#ffffff'
                          }),
                          indicatorsContainer: (provided) => ({
                            ...provided,
                            height: '31px'
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#9a9a9a'
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#ffffff'
                          }),
                          multiValue: (provided) => ({
                            ...provided,
                            backgroundColor: '#6c757d'
                          }),
                          multiValueLabel: (provided) => ({
                            ...provided,
                            color: '#ffffff'
                          }),
                          multiValueRemove: (provided) => ({
                            ...provided,
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#dc3545',
                              color: '#ffffff'
                            }
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: '#363636',
                            border: '1px solid #545454'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#6c757d' 
                              : state.isFocused 
                                ? '#404040' 
                                : '#363636',
                            color: state.isSelected ? '#ffffff' : '#ffffff',
                            '&:hover': {
                              backgroundColor: '#404040'
                            }
                          }),
                          noOptionsMessage: (provided) => ({
                            ...provided,
                            color: '#9a9a9a'
                          })
                        } : {
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: '31px',
                            fontSize: '14px'
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px'
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: '0px'
                          }),
                          indicatorsContainer: (provided) => ({
                            ...provided,
                            height: '31px'
                          })
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small">Site</label>
                      <input 
                        className="form-control form-control-sm" 
                        value={r.site} 
                        onChange={e=>update(i,'site',e.target.value)} 
                        placeholder="url" 
                      />
                    </div>
                    <div className="mb-3">
                      <PhotoUpload 
                        queryId={r.id}
                        onUploadSuccess={(url) => {
                          console.log('Photo uploaded successfully:', url);
                          // Optionally update the row with the photo URL
                        }}
                      />
                    </div>
                    <div className="d-flex justify-content-end">
                      <button 
                        className="btn btn-outline-danger btn-sm" 
                        disabled={rows.length===1} 
                        onClick={()=>handleDeleteClick(i)} 
                        title="Remove"
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop: Table layout with simplified animation
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>Time</th>
                <th style={{ 
                  width: expandedField === 'query' ? '50%' : 'auto',
                  transition: 'width 0.3s ease'
                }}>Query</th>
                <th style={{ 
                  width: expandedField === 'locations' ? '50%' : '200px',
                  transition: 'width 0.3s ease'
                }}>Locations</th>
                <th style={{ 
                  width: expandedField === 'site' ? '50%' : '150px',
                  transition: 'width 0.3s ease'
                }}>Site</th>
                <th style={{ width: '80px' }}>Max</th>
                <th style={{ width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <select 
                      className="form-select form-select-sm" 
                      value={r.timeFilter} 
                      onChange={e=>update(i,'timeFilter',e.target.value)}
                    >
                      <option value="any">Any time</option>
                      <option value="24h">Past day</option>
                      <option value="7d">Past week</option>
                    </select>
                  </td>
                  <td style={{ 
                    transition: 'width 0.3s ease',
                  }}>
                    <input 
                      className="form-control form-control-sm" 
                      value={r.query} 
                      onChange={e=>update(i,'query',e.target.value)} 
                      onFocus={() => handleFieldFocus('query')}
                      placeholder="Electric Cooperative" 
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td style={{ 
                    transition: 'width 0.3s ease',
                  }}>
                    <div
                      onFocus={() => handleFieldFocus('locations')}
                      style={{ width: '100%' }}
                    >
                      <Select
                        isMulti
                        options={locations.map(loc => ({ value: loc.id, label: loc.name }))}
                        value={r.locations.map(locId => ({ 
                          value: locId, 
                          label: locations.find(l => l.id === locId)?.name || 'Unknown' 
                        }))}
                        onChange={(selected) => update(i, 'locations', selected ? selected.map(s => s.value) : [])}
                        onFocus={() => handleFieldFocus('locations')}
                        placeholder="Select locations..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        theme={isDarkMode ? (theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: '#6c757d',
                            primary75: '#7a8288',
                            primary50: '#8a9299',
                            primary25: '#9aa1a8',
                            danger: '#dc3545',
                            dangerLight: '#f5c6cb',
                            neutral0: '#363636',
                            neutral5: '#404040',
                            neutral10: '#4a4a4a',
                            neutral20: '#545454',
                            neutral30: '#5e5e5e',
                            neutral40: '#686868',
                            neutral50: '#727272',
                            neutral60: '#7c7c7c',
                            neutral70: '#868686',
                            neutral80: '#909090',
                            neutral90: '#9a9a9a',
                          }
                        }) : undefined}
                        styles={isDarkMode ? {
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: '31px',
                            fontSize: '14px',
                            backgroundColor: '#363636',
                            borderColor: state.isFocused ? '#6c757d' : '#545454',
                            boxShadow: state.isFocused ? '0 0 0 1px #6c757d' : 'none',
                            '&:hover': {
                              borderColor: '#6c757d'
                            }
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px'
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: '0px',
                            color: '#ffffff'
                          }),
                          indicatorsContainer: (provided) => ({
                            ...provided,
                            height: '31px'
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#9a9a9a'
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#ffffff'
                          }),
                          multiValue: (provided) => ({
                            ...provided,
                            backgroundColor: '#6c757d'
                          }),
                          multiValueLabel: (provided) => ({
                            ...provided,
                            color: '#ffffff'
                          }),
                          multiValueRemove: (provided) => ({
                            ...provided,
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#dc3545',
                              color: '#ffffff'
                            }
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: '#363636',
                            border: '1px solid #545454'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#6c757d' 
                              : state.isFocused 
                                ? '#404040' 
                                : '#363636',
                            color: state.isSelected ? '#ffffff' : '#ffffff',
                            '&:hover': {
                              backgroundColor: '#404040'
                            }
                          }),
                          noOptionsMessage: (provided) => ({
                            ...provided,
                            color: '#9a9a9a'
                          })
                        } : {
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: '31px',
                            fontSize: '14px'
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px'
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: '0px'
                          }),
                          indicatorsContainer: (provided) => ({
                            ...provided,
                            height: '31px'
                          })
                        }}
                      />
                    </div>
                  </td>
                  <td style={{ 
                    transition: 'width 0.3s ease',
                  }}>
                    <input 
                      className="form-control form-control-sm" 
                      value={r.site} 
                      onChange={e=>update(i,'site',e.target.value)} 
                      onFocus={() => handleFieldFocus('site')}
                      placeholder="url" 
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td>
                    <input 
                      className="form-control form-control-sm" 
                      type="number" 
                      min={1} 
                      max={100} 
                      value={r.maxItems} 
                      onChange={e=>update(i,'maxItems',e.target.value)} 
                    />
                  </td>
                  <td>
                    <PhotoUpload 
                      queryId={r.id}
                      onUploadSuccess={(url) => {
                        console.log('Photo uploaded successfully:', url);
                        // Optionally update the row with the photo URL
                      }}
                    />
                    <button 
                      className="btn btn-outline-danger btn-sm" 
                      disabled={rows.length===1} 
                      onClick={()=>handleDeleteClick(i)} 
                      title="Remove"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <button className="btn btn-success" onClick={run} disabled={!hasQueries || running}>
          {running ? 'Running...' : 'Run Queries'}
        </button>
        <button className="btn btn-outline-success admin-query-margin" onClick={addRow} title="Add Query">
          +
        </button>
      </div>
      {msg && <div className="alert alert-success mt-3 py-2 mb-0">{msg}</div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block modal-overlay" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button 
                  type="button" 
                  className="modal-close-button" 
                  onClick={cancelDelete}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to remove this query?</p>
                <p className="text-muted small">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={confirmDelete}
                >
                  <i className="fas fa-trash me-2"></i>Delete Query
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={cancelDelete}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueriesSection;

